import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WorkspacePage from '../components/layout/WorkspacePage';
import Dashboard from '../components/Dashboard';
import DashboardErrorBoundary from '../components/DashboardErrorBoundary';
import ActionBriefing from '../components/dashboard/ActionBriefing';
import SummaryStrip from '../components/dashboard/SummaryStrip';
import DirectorAttendanceRiskPanel from '../components/director/DirectorAttendanceRiskPanel';
import TodayQueueWidget from '../components/today/TodayQueueWidget';
import DirectorAuditPanel from '../components/director/DirectorAuditPanel';
import DirectorBenchmarkPanel from '../components/director/DirectorBenchmarkPanel';
import DirectorDataManagementPanel from '../components/director/DirectorDataManagementPanel';
import DirectorDelegationsPanel from '../components/director/DirectorDelegationsPanel';
import DirectorGoalsPerformancePanel from '../components/director/DirectorGoalsPerformancePanel';
import DirectorIntegrationsPanel from '../components/director/DirectorIntegrationsPanel';
import DirectorKpisPanel from '../components/director/DirectorKpisPanel';
import DirectorLocationsPanel from '../components/director/DirectorLocationsPanel';
import DirectorManagersPanel from '../components/director/DirectorManagersPanel';
import DirectorPolicyAdminPanel from '../components/director/DirectorPolicyAdminPanel';
import SelectedKpiDetailPanel from '../components/director/SelectedKpiDetailPanel';
import SelectedKpiWorkflowActionBar from '../components/director/SelectedKpiWorkflowActionBar';
import DirectorUsersPanel from '../components/director/DirectorUsersPanel';
import DirectorRequestsSection from '../components/director/DirectorRequestsSection';
import { apiRequest } from '../services/apiClient';
import { useFetch } from '../hooks/useFetch';
import { withQuery, buildQuery } from '../utils/buildQuery';
import { alignPerformanceContextPeriod, createDefaultPerformanceContext } from '../components/director/performanceRows';
import { canAccessRoute, getRouteMeta, getScopeRollupLabel } from '../config/appRoutes';
import {
  buildRequestQueueSummary,
  canReviewRequest,
  filterRequestsByQueue,
  isAttendanceEvent,
  isVacationEvent
} from '../components/requests/requestUtils';
import { deriveDateRangeForPeriod } from '../utils/periodRange';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../utils/directorEvents';
import { buildPersonWorkspacePath } from '../config/workforceRouting';
import { useTenantModules } from '../context/TenantConfigContext';
import './RoleDashboards.css';

const DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY = 'director-attendance-focus-manager';
const DIRECTOR_PERFORMANCE_CONTEXT_STORAGE_KEY = 'director-performance-context';
const DIRECTOR_SELECTED_KPI_STORAGE_KEY = 'director-selected-kpi';
const DIRECTOR_SELECTED_KPI_META_STORAGE_KEY = 'director-selected-kpi-meta';
const DIRECTOR_SELECTED_KPI_SNAPSHOTS_STORAGE_KEY = 'director-selected-kpi-snapshots';
const DIRECTOR_SELECTED_KPI_CONTEXT_SIGNATURE_STORAGE_KEY = 'director-selected-kpi-context-signature';

function readStoredPerformanceContext() {
  const defaults = createDefaultPerformanceContext();
  try {
    const rawValue = window.sessionStorage.getItem(DIRECTOR_PERFORMANCE_CONTEXT_STORAGE_KEY);
    if (!rawValue) {
      return defaults;
    }
    const parsed = JSON.parse(rawValue);
    return {
      ...defaults,
      ...parsed
    };
  } catch (error) {
    return defaults;
  }
}

function readStoredSelectedKpiKey() {
  try {
    return window.sessionStorage.getItem(DIRECTOR_SELECTED_KPI_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
}

function readStoredSelectedKpiMeta() {
  try {
    const rawValue = window.sessionStorage.getItem(DIRECTOR_SELECTED_KPI_META_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
}

function readStoredSelectedKpiSnapshots() {
  try {
    const rawValue = window.sessionStorage.getItem(DIRECTOR_SELECTED_KPI_SNAPSHOTS_STORAGE_KEY);
    if (!rawValue) {
      return { kpiKey: '', goals: null, allocations: null, actuals: null };
    }
    const parsed = JSON.parse(rawValue);
    return {
      kpiKey: parsed?.kpiKey || '',
      goals: parsed?.goals || null,
      allocations: parsed?.allocations || null,
      actuals: parsed?.actuals || null
    };
  } catch (error) {
    return { kpiKey: '', goals: null, allocations: null, actuals: null };
  }
}

function readStoredSelectedKpiContextSignature() {
  try {
    return window.sessionStorage.getItem(DIRECTOR_SELECTED_KPI_CONTEXT_SIGNATURE_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
}

function getAllLocationsLabel(user) {
  return getScopeRollupLabel(user) || 'Company Rollup';
}

function buildPerformanceContextSignature(context = {}, selectedLocationCode = '') {
  return JSON.stringify({
    locationCode: selectedLocationCode && selectedLocationCode !== 'ALL'
      ? selectedLocationCode
      : (context.locationCode || ''),
    periodType: context.periodType || 'monthly',
    year: context.year || '',
    month: context.month || '',
    quarter: context.quarter || '',
    week: context.week || ''
  });
}

function getFallbackSelectedKpiMeta(snapshots = {}) {
  return snapshots.goals || snapshots.allocations || snapshots.actuals || null;
}

function countFuturePolicyDays(policies = [], policyTypes = []) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = new Set();

  policies.forEach((policy) => {
    if (!policy?.isActive || !policyTypes.includes(policy.policyType)) {
      return;
    }

    const start = new Date(`${policy.startDate}T00:00:00`);
    const end = new Date(`${(policy.endDate || policy.startDate)}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      if (date >= today) {
        days.add(date.toISOString().slice(0, 10));
      }
    }
  });

  return days.size;
}

function buildAttendanceFocusSubject(row = {}) {
  return {
    managerId: String(row.managerId || row.managerDbId || ''),
    employeeProfileId: String(row.employeeProfileId || ''),
    subjectLabel: row.displayName || row.managerName || row.name || ''
  };
}

function readStoredAttendanceFocusSubject() {
  try {
    const rawValue = window.sessionStorage.getItem(DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY) || '';
    if (!rawValue) {
      return null;
    }
    if (rawValue.startsWith('{')) {
      const parsed = JSON.parse(rawValue);
      return {
        managerId: String(parsed.managerId || ''),
        employeeProfileId: String(parsed.employeeProfileId || ''),
        subjectLabel: String(parsed.subjectLabel || '')
      };
    }
    return {
      managerId: rawValue,
      employeeProfileId: '',
      subjectLabel: ''
    };
  } catch (error) {
    return null;
  }
}

function hasAttendanceSubject(form = {}) {
  return Boolean(form.managerId || form.employeeProfileId);
}

function buildEventSubjectPayload(form = {}) {
  const managerId = form.managerId ? Number(form.managerId) : null;
  const employeeProfileId = form.employeeProfileId ? Number(form.employeeProfileId) : null;

  return {
    ...(managerId ? { managerId } : {}),
    ...(employeeProfileId ? { employeeProfileId } : {})
  };
}

function DirectorDashboard({
  authorityLabel,
  currentPath,
  dashboardData,
  period,
  managerOptions,
  selectedManager,
  allManagers,
  currentUserManager,
  locationDirectory,
  locationLabel,
  locationOwnerLabel,
  policies,
  requests,
  selectedLocationCode,
  stationSummary,
  onManagerChange,
  onNavigate,
  scopeLabel,
  user,
  selectedManagerDbId = '',
  userDisplayName = '',
  userReferenceTitle = '',
  workspaceLabel = 'Leadership Workspace'
}) {
  const { isModuleEnabled } = useTenantModules();
  const isDirectorDashboardRoute = currentPath === '/director-dashboard';
  const isRequestsCalendarRoute = currentPath === '/requests-calendar';
  const isAuditRoute = currentPath === '/audit';
  const isManagersRoute = currentPath === '/managers';
  const isUsersRoute = currentPath === '/users';
  const isGoalsRoute = currentPath === '/goals';
  const isAllocationsRoute = currentPath === '/allocations';
  const isActualsRoute = currentPath === '/actuals';
  const isLocationScorecardRoute = currentPath === '/location-scorecard';
  const isKpisRoute = currentPath === '/kpis';
  const isLocationsRoute = currentPath === '/locations';
  const isPoliciesRoute = currentPath === '/policies';
  const isIntegrationsRoute = currentPath === '/integrations';
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const [requestFilter, setRequestFilter] = useState('pending');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [requestSearch, setRequestSearch] = useState('');
  const [reviewNote, setReviewNote] = useState('');
  const [requestActionError, setRequestActionError] = useState('');
  const [requestActionLoading, setRequestActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [requestHistoryVersion, setRequestHistoryVersion] = useState(0);
  const [localRequests, setLocalRequests] = useState(requests);
  const [benchmarkRows, setBenchmarkRows] = useState([]);
  const [attendanceEvents, setAttendanceEvents] = useState([]);
  const [attendanceForm, setAttendanceForm] = useState({
    managerId: '',
    employeeProfileId: '',
    subjectLabel: '',
    eventType: 'sick_call',
    startDate: '',
    endDate: '',
    timeBlock: 'FULL DAY',
    reason: ''
  });
  const [manualPtoForm, setManualPtoForm] = useState({
    managerId: '',
    employeeProfileId: '',
    subjectLabel: '',
    startDate: '',
    endDate: '',
    timeBlock: 'FULL DAY',
    reason: ''
  });
  const [eventEntryMessage, setEventEntryMessage] = useState('');
  const [eventEntryError, setEventEntryError] = useState('');
  const [eventEntryLoading, setEventEntryLoading] = useState(false);
  const [performanceContext, setPerformanceContext] = useState(readStoredPerformanceContext);
  const [selectedKpiKey, setSelectedKpiKey] = useState(readStoredSelectedKpiKey);
  const [selectedKpiMeta, setSelectedKpiMeta] = useState(readStoredSelectedKpiMeta);
  const [selectedKpiSnapshots, setSelectedKpiSnapshots] = useState(readStoredSelectedKpiSnapshots);
  const [selectedKpiContextSignature, setSelectedKpiContextSignature] = useState(readStoredSelectedKpiContextSignature);
  const routeContent = getRouteMeta(currentPath, '/director-dashboard');
  const isPerformanceWorkflowRoute = isGoalsRoute || isAllocationsRoute || isActualsRoute;
  const canAccessCurrentRoute = canAccessRoute(user, currentPath);
  const allLocationsLabel = getAllLocationsLabel(user);
  const performanceContextSignature = useMemo(
    () => buildPerformanceContextSignature(performanceContext, selectedLocationCode),
    [performanceContext, selectedLocationCode]
  );

  useEffect(() => {
    try {
      window.sessionStorage.setItem(DIRECTOR_PERFORMANCE_CONTEXT_STORAGE_KEY, JSON.stringify(performanceContext));
    } catch (error) {
      // ignore storage issues
    }
  }, [performanceContext]);

  useEffect(() => {
    setPerformanceContext((current) => alignPerformanceContextPeriod(current, period));
  }, [period]);

  useEffect(() => {
    try {
      if (selectedKpiKey) {
        window.sessionStorage.setItem(DIRECTOR_SELECTED_KPI_STORAGE_KEY, selectedKpiKey);
      } else {
        window.sessionStorage.removeItem(DIRECTOR_SELECTED_KPI_STORAGE_KEY);
      }
    } catch (error) {
      // ignore storage issues
    }
  }, [selectedKpiKey]);

  useEffect(() => {
    try {
      if (selectedKpiMeta) {
        window.sessionStorage.setItem(DIRECTOR_SELECTED_KPI_META_STORAGE_KEY, JSON.stringify(selectedKpiMeta));
      } else {
        window.sessionStorage.removeItem(DIRECTOR_SELECTED_KPI_META_STORAGE_KEY);
      }
    } catch (error) {
      // ignore storage issues
    }
  }, [selectedKpiMeta]);

  useEffect(() => {
    try {
      if (selectedKpiSnapshots?.kpiKey) {
        window.sessionStorage.setItem(DIRECTOR_SELECTED_KPI_SNAPSHOTS_STORAGE_KEY, JSON.stringify(selectedKpiSnapshots));
      } else {
        window.sessionStorage.removeItem(DIRECTOR_SELECTED_KPI_SNAPSHOTS_STORAGE_KEY);
      }
    } catch (error) {
      // ignore storage issues
    }
  }, [selectedKpiSnapshots]);

  useEffect(() => {
    try {
      if (selectedKpiContextSignature) {
        window.sessionStorage.setItem(DIRECTOR_SELECTED_KPI_CONTEXT_SIGNATURE_STORAGE_KEY, selectedKpiContextSignature);
      } else {
        window.sessionStorage.removeItem(DIRECTOR_SELECTED_KPI_CONTEXT_SIGNATURE_STORAGE_KEY);
      }
    } catch (error) {
      // ignore storage issues
    }
  }, [selectedKpiContextSignature]);

  useEffect(() => {
    if (selectedLocationCode && selectedLocationCode !== 'ALL' && performanceContext.locationCode !== selectedLocationCode) {
      setPerformanceContext(current => ({
        ...current,
        locationCode: selectedLocationCode
      }));
    }
  }, [performanceContext.locationCode, selectedLocationCode]);

  const handlePerformanceContextChange = (field, value) => {
    const defaults = createDefaultPerformanceContext();
    setPerformanceContext(current => ({
      ...current,
      [field]: value,
      ...(field === 'periodType'
        ? { month: defaults.month, quarter: defaults.quarter, week: defaults.week }
        : {})
    }));
  };

  const clearSelectedKpiWorkflow = useCallback(() => {
    setSelectedKpiKey('');
    setSelectedKpiMeta(null);
    setSelectedKpiSnapshots({ kpiKey: '', goals: null, allocations: null, actuals: null });
    setSelectedKpiContextSignature('');
  }, []);

  useEffect(() => {
    if (selectedKpiKey && (!selectedKpiContextSignature || selectedKpiContextSignature !== performanceContextSignature)) {
      clearSelectedKpiWorkflow();
    }
  }, [clearSelectedKpiWorkflow, performanceContextSignature, selectedKpiContextSignature, selectedKpiKey]);

  const handleSelectKpi = useCallback((selection) => {
    if (!selection) {
      clearSelectedKpiWorkflow();
      return;
    }

    if (typeof selection === 'string') {
      if (selectedKpiKey && selectedKpiKey !== selection) {
        setSelectedKpiSnapshots({ kpiKey: selection, goals: null, allocations: null, actuals: null });
      }
      setSelectedKpiKey(selection);
      setSelectedKpiContextSignature(performanceContextSignature);
      return;
    }

    const nextKey = selection.kpiKey || '';
    setSelectedKpiKey(nextKey);
    setSelectedKpiMeta((current) => {
      if (
        current
        && current.kpiKey === selection.kpiKey
        && current.kpiLabel === selection.kpiLabel
        && current.category === selection.category
        && current.goalName === selection.goalName
        && current.stage === selection.stage
        && current.statusLabel === selection.statusLabel
        && current.statusTone === selection.statusTone
        && current.statusDetail === selection.statusDetail
      ) {
        return current;
      }
      return selection;
    });
    setSelectedKpiSnapshots((current) => (
      current.kpiKey && current.kpiKey !== nextKey
        ? { kpiKey: nextKey, goals: null, allocations: null, actuals: null }
        : current
    ));
    setSelectedKpiContextSignature(performanceContextSignature);
  }, [clearSelectedKpiWorkflow, performanceContextSignature, selectedKpiKey]);

  const handleUpdateSelectedKpiSnapshot = useCallback((snapshot) => {
    if (!snapshot?.stage || !snapshot?.kpiKey) {
      return;
    }

    if (snapshot.clear) {
      setSelectedKpiSnapshots((current) => {
        if (current.kpiKey !== snapshot.kpiKey) {
          return current;
        }

        const nextSnapshots = {
          ...current,
          [snapshot.stage]: null
        };

        if (!nextSnapshots.goals && !nextSnapshots.allocations && !nextSnapshots.actuals) {
          return { kpiKey: current.kpiKey, goals: null, allocations: null, actuals: null };
        }

        return nextSnapshots;
      });
      return;
    }

    setSelectedKpiSnapshots((current) => {
      if (current.kpiKey !== snapshot.kpiKey) {
        return {
          kpiKey: snapshot.kpiKey,
          goals: snapshot.stage === 'goals' ? snapshot : null,
          allocations: snapshot.stage === 'allocations' ? snapshot : null,
          actuals: snapshot.stage === 'actuals' ? snapshot : null
        };
      }

      return {
        ...current,
        [snapshot.stage]: snapshot
      };
    });
  }, []);

  useEffect(() => {
    if (!selectedKpiKey) {
      if (selectedKpiMeta) {
        setSelectedKpiMeta(null);
      }
      return;
    }

    const fallbackMeta = getFallbackSelectedKpiMeta(selectedKpiSnapshots);
    if (!selectedKpiMeta) {
      if (fallbackMeta?.kpiKey === selectedKpiKey) {
        setSelectedKpiMeta(fallbackMeta);
      }
      return;
    }

    if (selectedKpiMeta.kpiKey !== selectedKpiKey) {
      setSelectedKpiMeta(fallbackMeta?.kpiKey === selectedKpiKey ? fallbackMeta : null);
      return;
    }

    if (selectedKpiMeta.stage && !selectedKpiSnapshots?.[selectedKpiMeta.stage]) {
      setSelectedKpiMeta(fallbackMeta?.kpiKey === selectedKpiKey ? fallbackMeta : null);
    }
  }, [selectedKpiKey, selectedKpiMeta, selectedKpiSnapshots]);

  React.useEffect(() => {
    setLocalRequests(requests);
  }, [requests]);

  React.useEffect(() => {
    if (!allManagers.length) {
      return;
    }

    const focusedSubject = readStoredAttendanceFocusSubject();
    const defaultManagerId = focusedSubject?.managerId || String(allManagers[0].managerDbId);

    setAttendanceForm((current) => (
      hasAttendanceSubject(current) ? current : { ...current, managerId: defaultManagerId }
    ));
    setManualPtoForm((current) => (
      hasAttendanceSubject(current) ? current : { ...current, managerId: defaultManagerId }
    ));

    if (focusedSubject) {
      setAttendanceForm((current) => ({
        ...current,
        managerId: focusedSubject.managerId,
        employeeProfileId: focusedSubject.employeeProfileId,
        subjectLabel: focusedSubject.subjectLabel
      }));
      setManualPtoForm((current) => ({
        ...current,
        managerId: focusedSubject.managerId,
        employeeProfileId: focusedSubject.employeeProfileId,
        subjectLabel: focusedSubject.subjectLabel
      }));
      try {
        window.sessionStorage.removeItem(DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY);
      } catch (error) {
        // ignore storage cleanup issues
      }
    }
  }, [allManagers]);

  const benchmarkUrl = useMemo(() => withQuery('/api/benchmarks/vacation', {
    locationCode: selectedLocationCode && selectedLocationCode !== 'ALL' ? selectedLocationCode : null
  }), [selectedLocationCode]);

  const { data: benchmarkPayload, refetch: refetchBenchmark } = useFetch(benchmarkUrl);

  useEffect(() => {
    setBenchmarkRows(Array.isArray(benchmarkPayload?.rows) ? benchmarkPayload.rows : []);
  }, [benchmarkPayload]);

  useEffect(() => {
    const handler = () => refetchBenchmark();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetchBenchmark]);

  const attendanceUrl = useMemo(() => {
    const { startDate, endDate } = deriveDateRangeForPeriod(period);
    const scopedLocation = selectedLocationCode && selectedLocationCode !== 'ALL'
      ? locationDirectory.find(location => location.code === selectedLocationCode)
      : null;
    const qs = buildQuery({
      startDate,
      endDate,
      locationId: scopedLocation?.id || null
    });
    return `/api/events/attendance?${qs}`;
  }, [locationDirectory, period, selectedLocationCode]);

  const { data: attendancePayload, refetch: refetchAttendance } = useFetch(attendanceUrl);

  useEffect(() => {
    setAttendanceEvents(Array.isArray(attendancePayload) ? attendancePayload : []);
  }, [attendancePayload]);

  useEffect(() => {
    const handler = () => refetchAttendance();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetchAttendance]);

  const requestSummary = useMemo(() => buildRequestQueueSummary(localRequests), [localRequests]);
  const ownRequestOutsideScope = useMemo(
    () => Boolean(
      currentUserManager
      && selectedLocationCode !== 'ALL'
      && currentUserManager.locationCode !== selectedLocationCode
    ),
    [currentUserManager, selectedLocationCode]
  );
  const managerEntryOptions = useMemo(
    () => allManagers.map(manager => ({
      value: String(manager.managerDbId),
      label: `${manager.name} (${manager.locationCode})`
    })),
    [allManagers]
  );

  const selectedRequest = useMemo(
    () => localRequests.find(request => String(request.id) === String(selectedRequestId)) || null,
    [localRequests, selectedRequestId]
  );

  const filteredRequests = useMemo(() => {
    const queueFiltered = filterRequestsByQueue(localRequests, requestFilter);

    return queueFiltered.filter((request) => {
      if (requestTypeFilter === 'attendance' && !isAttendanceEvent(request)) {
        return false;
      }
      if (requestTypeFilter === 'pto' && !isVacationEvent(request)) {
        return false;
      }
      if (!requestSearch.trim()) {
        return true;
      }

      const searchValue = requestSearch.trim().toLowerCase();
      return [
        request.managerName,
        request.locationCode,
        request.reason,
        request.status,
        request.eventType
      ].some(value => String(value || '').toLowerCase().includes(searchValue));
    });
  }, [localRequests, requestFilter, requestSearch, requestTypeFilter]);

  React.useEffect(() => {
    if (!filteredRequests.some(request => String(request.id) === String(selectedRequestId))) {
      setSelectedRequestId('');
      setReviewNote('');
      setRequestActionError('');
    }
  }, [filteredRequests, selectedRequestId]);

  const selectedRequestCanReview = canReviewRequest(selectedRequest);
  const aboveBenchmarkCount = useMemo(
    () => benchmarkRows.filter(row => row.benchmarkFlag === 'Above Benchmark').length,
    [benchmarkRows]
  );
  const upcomingRiskDays = useMemo(
    () => countFuturePolicyDays(policies, ['blackout_period', 'max_managers_off']),
    [policies]
  );
  const requestsEnabled = isModuleEnabled('requests');
  const attendanceEnabled = isModuleEnabled('attendance');
  const tasksEnabled = isModuleEnabled('tasks');
  const summaryItems = useMemo(() => ([
    {
      label: 'Locations In Scope',
      value: stationSummary.stationCount,
      subtitle: 'Active locations visible in the current director scope',
      tone: 'good',
      icon: 'fa-building-circle-check'
    },
    {
      label: 'Managers In Scope',
      value: allManagers.length,
      subtitle: 'Active managers visible for the selected location scope',
      tone: 'neutral',
      icon: 'fa-users'
    },
    ...(requestsEnabled ? [
      {
        label: 'Benchmark Alerts',
        value: aboveBenchmarkCount,
        subtitle: 'Managers above the benchmark vacation reference',
        tone: 'warning',
        icon: 'fa-user-clock'
      },
      {
        label: 'Pending Approvals',
        value: requestSummary.pending + requestSummary.reviewNeeded,
        subtitle: 'Requests still waiting on leadership action',
        tone: 'info',
        icon: 'fa-clipboard-check'
      },
      {
        label: 'Upcoming Blackout / Risk Days',
        value: upcomingRiskDays,
        subtitle: 'Future blackout or coverage rule days in scope',
        tone: 'neutral',
        icon: 'fa-calendar-xmark'
      }
    ] : [])
  ]), [aboveBenchmarkCount, allManagers.length, requestSummary.pending, requestSummary.reviewNeeded, requestsEnabled, stationSummary.stationCount, upcomingRiskDays]);

  const openDirectorAttendanceFlow = (managerRow, mode = 'attendance') => {
    const subject = buildAttendanceFocusSubject(managerRow);
    if (subject.managerId || subject.employeeProfileId) {
      try {
        window.sessionStorage.setItem(DIRECTOR_ATTENDANCE_FOCUS_STORAGE_KEY, JSON.stringify(subject));
      } catch (error) {
        // ignore storage issues
      }
    }

    if (subject.managerId) {
      onManagerChange?.(subject.managerId);
    }
    if (currentPath !== '/requests-calendar') {
      onNavigate?.('/requests-calendar');
      return;
    }

    if (mode === 'attendance') {
      setAttendanceForm((current) => ({ ...current, ...subject }));
    } else {
      setManualPtoForm((current) => ({ ...current, ...subject }));
    }
  };

  const handleDirectorVacationSubmitted = (createdEvent) => {
    if (!createdEvent) {
      return;
    }

    setLocalRequests(current => [createdEvent, ...current.filter(eventRecord => String(eventRecord.id) !== String(createdEvent.id))]);
    setRequestFilter('all');
    setRequestTypeFilter('pto');
    setSelectedRequestId(String(createdEvent.id));
    notifyDirectorAdminToast(
      String(createdEvent.status || '').toLowerCase() === 'approved'
        ? 'Your PTO request was auto-approved.'
        : `Request submitted with status: ${createdEvent.status || 'Pending'}.`
    );
    notifyDirectorDataUpdated('Director request and calendar data refreshed.');
  };

  const handleAttendanceSubmit = async (event) => {
    event.preventDefault();
    setEventEntryLoading(true);
    setEventEntryMessage('');
    setEventEntryError('');

    try {
      const createdEvent = await apiRequest('/api/events/attendance', {
        method: 'POST',
        body: JSON.stringify({
          ...attendanceForm,
          ...buildEventSubjectPayload(attendanceForm)
        })
      });

      setAttendanceForm(current => ({
        ...current,
        startDate: '',
        endDate: '',
        reason: ''
      }));
      setAttendanceEvents(current => [createdEvent, ...current.filter(eventRecord => String(eventRecord.id) !== String(createdEvent.id))]);
      setLocalRequests(current => [createdEvent, ...current.filter(eventRecord => String(eventRecord.id) !== String(createdEvent.id))]);
      setRequestFilter('all');
      setRequestTypeFilter('attendance');
      setSelectedRequestId(String(createdEvent.id));
      setEventEntryMessage('Attendance event saved.');
      notifyDirectorAdminToast('Attendance event saved.');
      notifyDirectorDataUpdated('Director request and attendance data refreshed.');
    } catch (error) {
      setEventEntryError(error.message || 'Unable to record attendance event.');
      notifyDirectorAdminToast(error.message || 'Unable to record attendance event.', 'error');
    } finally {
      setEventEntryLoading(false);
    }
  };

  const handleManualPtoSubmit = async (event) => {
    event.preventDefault();
    setEventEntryLoading(true);
    setEventEntryMessage('');
    setEventEntryError('');

    try {
      const createdEvent = await apiRequest('/api/events/manual-pto', {
        method: 'POST',
        body: JSON.stringify({
          ...manualPtoForm,
          ...buildEventSubjectPayload(manualPtoForm)
        })
      });

      setManualPtoForm(current => ({
        ...current,
        startDate: '',
        endDate: '',
        reason: ''
      }));
      setLocalRequests(current => [createdEvent, ...current.filter(eventRecord => String(eventRecord.id) !== String(createdEvent.id))]);
      setRequestFilter('all');
      setRequestTypeFilter('pto');
      setSelectedRequestId(String(createdEvent.id));
      setEventEntryMessage('Manual PTO saved.');
      notifyDirectorAdminToast('Manual PTO saved.');
      notifyDirectorDataUpdated('Director request and attendance data refreshed.');
    } catch (error) {
      setEventEntryError(error.message || 'Unable to record manual PTO.');
      notifyDirectorAdminToast(error.message || 'Unable to record manual PTO.', 'error');
    } finally {
      setEventEntryLoading(false);
    }
  };

  const handleInlineDecision = async (request, decision) => {
    if (!request?.id || requestActionLoading) return;
    if (decision === 'deny') {
      setSelectedRequestId(String(request.id));
      return;
    }
    setPendingAction(decision);
    setRequestActionLoading(true);
    setRequestActionError('');
    try {
      const updatedRequest = await apiRequest('/api/events/vacation-approve', {
        method: 'POST',
        body: JSON.stringify({ id: request.id })
      });
      setLocalRequests(current => current.map(r =>
        String(r.id) === String(updatedRequest.id) ? { ...r, ...updatedRequest } : r
      ));
      setRequestHistoryVersion(current => current + 1);
      notifyDirectorAdminToast(`Approved request for ${request.managerName}.`);
      notifyDirectorDataUpdated('Director request data refreshed.');
    } catch (error) {
      const nextMessage = error.message || 'Unable to approve request.';
      setRequestActionError(nextMessage);
      notifyDirectorAdminToast(nextMessage, 'error');
    } finally {
      setRequestActionLoading(false);
      setPendingAction('');
    }
  };

  const handleRequestDecision = async (decision) => {
    if (!selectedRequest?.id || requestActionLoading) {
      return;
    }

    setPendingAction(decision);
    setRequestActionLoading(true);
    setRequestActionError('');

    try {
      const path = decision === 'approve' ? '/api/events/vacation-approve' : '/api/events/vacation-deny';
      const payload = decision === 'approve'
        ? { id: selectedRequest.id }
        : { id: selectedRequest.id, reason: reviewNote || null };

      const updatedRequest = await apiRequest(path, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      setLocalRequests(current => current.map(request => (
        String(request.id) === String(updatedRequest.id) ? { ...request, ...updatedRequest } : request
      )));
      setRequestHistoryVersion(current => current + 1);
      notifyDirectorAdminToast(
        decision === 'approve'
          ? `Approved request for ${selectedRequest.managerName}.`
          : `Denied request for ${selectedRequest.managerName}.`
      );
      notifyDirectorDataUpdated('Director request data refreshed.');
      if (decision === 'deny') {
        setReviewNote('');
      }
    } catch (error) {
      const nextMessage = error.message || `Unable to ${decision} request.`;
      setRequestActionError(nextMessage);
      notifyDirectorAdminToast(nextMessage, 'error');
    } finally {
      setRequestActionLoading(false);
      setPendingAction('');
    }
  };

  // Sprint 3B.1: DirectorDashboard migrated to WorkspacePage with
  // width="operational". The role-hero header anatomy (badge,
  // category, scope chips, title, description) maps to:
  //   eyebrow      → badge + category
  //   title        → routeContent.title
  //   subtitle     → routeContent.description
  //   contextSlot  → scope-chip row (Profile / Title / Scope /
  //                  Permissions / Primary Owner)
  // The .role-page class is retained on a child wrapper so all the
  // existing role-* CSS selectors still apply to the dashboard body
  // (summary-grid, role-table, etc.) without a stylesheet rewrite.
  const headerEyebrow = (
    <>
      <span className="role-badge">{workspaceLabel}</span>
      {routeContent.category ? (
        <span className="role-hero-context">{routeContent.category}</span>
      ) : null}
    </>
  );

  const headerContextChips = (
    <div className="role-context-row">
      <div className="role-scope-chip" title="The signed-in person using this workspace.">
        <span>Profile</span>
        <strong>{userDisplayName || user?.email || 'Authenticated user'}</strong>
      </div>
      {userReferenceTitle ? (
        <div className="role-scope-chip" title="Job title stored for reference.">
          <span>Title</span>
          <strong>{userReferenceTitle}</strong>
        </div>
      ) : null}
      <div className="role-scope-chip" title="The location or group of locations your view is scoped to. Data outside this scope is not visible.">
        <span>Scope</span>
        <strong>{scopeLabel || locationLabel}</strong>
      </div>
      {authorityLabel ? (
        <div className="role-scope-chip" title="Additional permissions granted to this account, such as cross-location access or escalated approval rights.">
          <span>Permissions</span>
          <strong>{authorityLabel}</strong>
        </div>
      ) : null}
      {locationOwnerLabel ? (
        <div className="role-scope-chip" title="The owner of record for this location — responsible for final approvals and location-level goals.">
          <span>Primary Owner</span>
          <strong>{locationOwnerLabel}</strong>
        </div>
      ) : null}
    </div>
  );

  return (
    <WorkspacePage
      data-workspace-id="director-dashboard"
      width="operational"
      eyebrow={headerEyebrow}
      title={routeContent.title}
      subtitle={routeContent.description}
      contextSlot={headerContextChips}
    >
      <div className="role-page">
        <SummaryStrip items={summaryItems} />

      {isDirectorDashboardRoute && requestsEnabled && (
        <ActionBriefing
          heading="Open action items"
          items={[
            { count: requestSummary.pending, label: 'vacation request action items awaiting approval', tone: 'warning', path: '/requests-calendar', onNavigate },
            { count: requestSummary.reviewNeeded, label: 'coverage policy action items to review', tone: 'info', path: '/requests-calendar', onNavigate },
            { count: upcomingRiskDays, label: 'upcoming blackout or coverage action items', tone: 'neutral', path: '/requests-calendar', onNavigate }
          ]}
        />
      )}

      {isDirectorDashboardRoute && (
        <div className="summary-grid">
          <div className="summary-card">
            <span className="summary-card-label">Locations Visible</span>
            <strong>{stationSummary.stationCount}</strong>
            <p>All active locations in scope</p>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">People Visible</span>
            <strong>{allManagers.length}</strong>
            <p>{locationLabel} people scope</p>
          </div>
          <div className="summary-card">
            <span className="summary-card-label">Command Focus</span>
            <strong>{isDirectorDashboardRoute ? 'Live' : isRequestsCalendarRoute ? 'Workflow' : 'Admin'}</strong>
            <p>{isDirectorDashboardRoute ? 'Monitoring, benchmark, risk, and scorecard review' : isRequestsCalendarRoute ? 'Approvals, request history, and shared calendar visibility' : 'Configuration, setup, and audit-safe administration'}</p>
          </div>
          {requestsEnabled ? (
            <div className="summary-card">
              <span className="summary-card-label">Live Vacation Requests</span>
              <strong>{requests.length}</strong>
              <p>{requestSummary.pending} pending, {requestSummary.approved} approved, {requestSummary.denied} denied</p>
            </div>
          ) : null}
        </div>
      )}

      {isPerformanceWorkflowRoute && canAccessCurrentRoute && selectedKpiKey ? (
        <>
          <SelectedKpiWorkflowActionBar
            currentPath={currentPath}
            onNavigate={onNavigate}
            selectedKpiKey={selectedKpiKey}
            selectedKpiMeta={selectedKpiMeta}
          />
          <SelectedKpiDetailPanel
            selectedKpiKey={selectedKpiKey}
            selectedKpiMeta={selectedKpiMeta}
            selectedKpiSnapshots={selectedKpiSnapshots}
          />
        </>
      ) : null}

      {!canAccessCurrentRoute ? (
        <section className="director-card">
          <div className="role-section-header">
            <h2>Access Restricted</h2>
            <span>{routeContent.category}</span>
          </div>
          <p className="role-section-copy">
            This section requires an additional permission. This account can still work inside its
            assigned scope, but this area stays locked until an authorized administrator grants
            access.
          </p>
        </section>
      ) : null}

      {canAccessCurrentRoute && isAuditRoute && (
        <>
          <DirectorDataManagementPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
            performanceContext={performanceContext}
            managerRows={allManagers}
            requestRows={localRequests}
            attendanceRows={attendanceEvents}
            benchmarkRows={benchmarkRows}
            onNavigate={onNavigate}
          />
          <DirectorAuditPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {canAccessCurrentRoute && isManagersRoute && (
        <DirectorManagersPanel
          locationLabel={locationLabel}
          selectedLocationCode={selectedLocationCode}
          onNavigate={onNavigate}
        />
      )}

      {canAccessCurrentRoute && isUsersRoute && (
        <>
          <DirectorUsersPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
          <DirectorDelegationsPanel
            locationLabel={locationLabel}
            selectedLocationCode={selectedLocationCode}
          />
        </>
      )}

      {canAccessCurrentRoute && isPerformanceWorkflowRoute && (
        <DirectorGoalsPerformancePanel
          locationLabel={locationLabel}
          selectedLocationCode={selectedLocationCode}
          performanceContext={performanceContext}
          onPerformanceContextChange={handlePerformanceContextChange}
          selectedManagerIdExternal={String(selectedManagerDbId || '')}
          onSelectedManagerChange={onManagerChange}
          selectedKpiKey={selectedKpiKey}
          onSelectKpi={handleSelectKpi}
          onUpdateSelectedKpiSnapshot={handleUpdateSelectedKpiSnapshot}
          defaultStage={isActualsRoute ? 'actuals' : isAllocationsRoute ? 'allocations' : 'goals'}
        />
      )}

      {canAccessCurrentRoute && isKpisRoute && <DirectorKpisPanel />}

      {canAccessCurrentRoute && isLocationsRoute && <DirectorLocationsPanel onNavigate={onNavigate} />}

      {canAccessCurrentRoute && isPoliciesRoute && (
        <DirectorPolicyAdminPanel
          locationLabel={locationLabel}
          locations={locationDirectory}
          policies={policies}
          selectedLocationCode={selectedLocationCode}
        />
      )}

      {canAccessCurrentRoute && isIntegrationsRoute && <DirectorIntegrationsPanel />}

      {isRequestsCalendarRoute && (
        <DirectorRequestsSection
          filteredRequests={filteredRequests}
          requestFilter={requestFilter}
          setRequestFilter={setRequestFilter}
          requestSummary={requestSummary}
          requestSearch={requestSearch}
          setRequestSearch={setRequestSearch}
          requestTypeFilter={requestTypeFilter}
          setRequestTypeFilter={setRequestTypeFilter}
          selectedRequestId={selectedRequestId}
          setSelectedRequestId={setSelectedRequestId}
          selectedRequest={selectedRequest}
          selectedRequestCanReview={selectedRequestCanReview}
          requestActionError={requestActionError}
          requestActionLoading={requestActionLoading}
          pendingAction={pendingAction}
          handleRequestDecision={handleRequestDecision}
          reviewNote={reviewNote}
          setReviewNote={setReviewNote}
          requestHistoryVersion={requestHistoryVersion}
          ownRequestOutsideScope={ownRequestOutsideScope}
          currentUserManager={currentUserManager}
          allLocationsLabel={allLocationsLabel}
          handleDirectorVacationSubmitted={handleDirectorVacationSubmitted}
          eventEntryError={eventEntryError}
          eventEntryMessage={eventEntryMessage}
          eventEntryLoading={eventEntryLoading}
          attendanceForm={attendanceForm}
          setAttendanceForm={setAttendanceForm}
          manualPtoForm={manualPtoForm}
          setManualPtoForm={setManualPtoForm}
          handleAttendanceSubmit={handleAttendanceSubmit}
          handleManualPtoSubmit={handleManualPtoSubmit}
          managerEntryOptions={managerEntryOptions}
          selectedLocationCode={selectedLocationCode}
          locationLabel={locationLabel}
          user={user}
          onInlineDecision={handleInlineDecision}
          onNavigate={onNavigate}
        />
      )}

      {isDirectorDashboardRoute && (
        <>
          {attendanceEnabled ? (
            <DirectorAttendanceRiskPanel
              locationLabel={locationLabel}
              selectedLocationCode={selectedLocationCode}
              onManagerChange={onManagerChange}
              onOpenPersonWorkspace={(row) => {
                onNavigate?.(buildPersonWorkspacePath({
                  employeeProfileId: row.employeeProfileId,
                  managerId: row.managerId
                }));
              }}
              onOpenAttendanceEntry={(row) => openDirectorAttendanceFlow(row, 'attendance')}
              onOpenManualPtoEntry={(row) => openDirectorAttendanceFlow(row, 'manualPto')}
            />
          ) : null}

          {requestsEnabled ? (
            <DirectorBenchmarkPanel
              locationLabel={locationLabel}
              selectedLocationCode={selectedLocationCode}
            />
          ) : null}

          {tasksEnabled ? (
            <TodayQueueWidget
              title="Today"
              subtitle="Top focus items across approvals, KPIs, actuals, attendance, and action items."
              onNavigate={onNavigate}
            />
          ) : null}
        </>
      )}

      {canAccessCurrentRoute && isLocationScorecardRoute && (
        <>
          <div className="role-section">
            <div className="role-section-header">
              <h2>Location Scorecard</h2>
              <span>{locationLabel}</span>
            </div>
            <div className="director-scorecard-meta">
              <span className="director-scorecard-meta-pill">Location {locationLabel}</span>
              <span className="director-scorecard-meta-pill">Period {period}</span>
              <span className="director-scorecard-meta-pill">Manager View {managerOptions.find(manager => String(manager.id) === String(selectedManager))?.name || 'Select manager'}</span>
            </div>
          </div>

          <DashboardErrorBoundary
            resetKey={`${selectedLocationCode}:${selectedManager}:${period}`}
            title="Director scorecard view hit an unexpected error."
            message="Change the location, scorecard view, or time period to reload this section."
          >
            <Dashboard
              data={dashboardData}
              period={period}
              manager={selectedManager}
              dataVersion={0}
              scorecardContext={performanceContext}
              variant="director_scorecard"
              onManagerDrilldown={(managerId) => {
                onManagerChange?.(managerId);
                onNavigate?.('/location-scorecard');
              }}
              onNavigate={onNavigate}
            />
          </DashboardErrorBoundary>
        </>
      )}
      </div>
    </WorkspacePage>
  );
}

export default DirectorDashboard;
